import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, AlertCircle, Info, Check, RefreshCw } from 'lucide-react';
import { useAlerts, useMarkAlertRead, useGenerateAlerts } from '../hooks/useDashboard.js';
import { useAuth } from '../../../core/auth/AuthContext.js';
import { isOwnerRole } from '../../../core/auth/roles.js';

const ICON_MAP: Record<string, typeof AlertTriangle> = {
  URGENT: AlertTriangle,
  WAARSCHUWING: AlertCircle,
  INFO: Info,
};

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; icon: string }> = {
  URGENT: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-500' },
  WAARSCHUWING: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-500' },
  INFO: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'text-blue-500' },
};

const LINK_MAP: Record<string, string> = {
  DOSSIER: '/dossiers',
  LEAD: '/crm/leads',
  VORDERINGSSTAAT: '/logistiek/vorderingsstaten',
  TICKET: '/buitendienst',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}u geleden`;
  const days = Math.floor(hours / 24);
  return `${days}d geleden`;
}

export function AlertPanel() {
  const { user } = useAuth();
  const isAdmin = isOwnerRole(user?.role);
  const { data, isLoading } = useAlerts();
  const markRead = useMarkAlertRead();
  const generate = useGenerateAlerts();
  const navigate = useNavigate();

  if (isLoading || !data) return null;
  if (data.alerts.length === 0 && !isAdmin) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-gold" />
          <h3 className="text-sm font-semibold text-gray-900">Alerts</h3>
          {data.ongelezenCount > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {data.ongelezenCount}
            </span>
          )}
        </div>
        {isAdmin && (
          <button
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            className="text-xs text-gray-400 hover:text-gold transition-colors flex items-center gap-1"
          >
            <RefreshCw size={12} className={generate.isPending ? 'animate-spin' : ''} />
            Genereer
          </button>
        )}
      </div>
      <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
        {data.alerts.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">Geen alerts</div>
        ) : data.alerts.map(alert => {
          const colors = COLOR_MAP[alert.prioriteit] || COLOR_MAP.INFO;
          const Icon = ICON_MAP[alert.prioriteit] || Info;
          return (
            <div
              key={alert.id}
              className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${!alert.isGelezen ? 'bg-gold/5' : ''}`}
              onClick={() => {
                if (!alert.isGelezen) markRead.mutate(alert.id);
                if (alert.linkedEntityType && alert.linkedEntityId) {
                  const base = LINK_MAP[alert.linkedEntityType] || '';
                  if (base) navigate(`${base}/${alert.linkedEntityId}`);
                }
              }}
            >
              <Icon size={16} className={`${colors.icon} flex-shrink-0 mt-0.5`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${!alert.isGelezen ? 'text-gray-900' : 'text-gray-600'}`}>
                  {alert.titel}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{alert.beschrijving}</p>
                <p className="text-[10px] text-gray-400 mt-1">{timeAgo(alert.createdAt)}</p>
              </div>
              {!alert.isGelezen && (
                <button
                  onClick={(e) => { e.stopPropagation(); markRead.mutate(alert.id); }}
                  className="text-gray-300 hover:text-green-500 transition-colors flex-shrink-0"
                >
                  <Check size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AlertBadge() {
  const { data } = useAlerts(true);
  if (!data || data.ongelezenCount === 0) return null;
  return (
    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-[16px] rounded-full flex items-center justify-center px-0.5">
      {data.ongelezenCount > 99 ? '99+' : data.ongelezenCount}
    </span>
  );
}
